import React, { useEffect, useState } from 'react'
import useIsResponsive from '@/hooks/useIsResponsive'
import { Box } from '@mui/material'
import { EmtButton, EmtModal, EmtTypography } from 'emt-components-v2'
import AnimalIncrement from '@/components/PetSitter/AnimalIncrement'
import { SignUpCallRequestAnimalSelection } from '@/store/signUpCallRequestStore'

interface EditAnimalSelectionModalProps {
  open: boolean
  setOpen: (open: boolean) => void
  animalSelection?: SignUpCallRequestAnimalSelection
  onSave: (selection: SignUpCallRequestAnimalSelection) => void
}

const animalSlugs = ['dog', 'cat', 'nac'] as const

const getCompleteSelection = (selection?: SignUpCallRequestAnimalSelection): SignUpCallRequestAnimalSelection =>
  animalSlugs.map((slug) => ({
    slug,
    number: selection?.find((animal) => animal.slug === slug)?.number ?? 0,
  }))

const EditAnimalSelectionModal: React.FC<EditAnimalSelectionModalProps> = ({
  open,
  setOpen,
  animalSelection,
  onSave,
}) => {
  const [isMobile] = useIsResponsive()
  const [selection, setSelection] = useState(() => getCompleteSelection(animalSelection))
  const selectedCount = selection.reduce((total, animal) => total + animal.number, 0)

  useEffect(() => {
    if (open) setSelection(getCompleteSelection(animalSelection))
  }, [open, animalSelection])

  const handleCountChange = (slug: (typeof animalSlugs)[number], delta: number) => {
    setSelection((current) =>
      current.map((animal) =>
        animal.slug === slug ? { ...animal, number: Math.max(0, animal.number + delta) } : animal,
      ),
    )
  }

  const handleSave = () => {
    onSave(selection)
    setOpen(false)
  }

  return (
    <EmtModal
      open={open}
      onClose={(event: React.MouseEvent) => {
        event.stopPropagation()
        setOpen(false)
      }}
      sx={{
        '& .inner-box': { width: '600px', maxWidth: '90vw' },
        '& .children-box': { padding: isMobile ? '26px' : '40px' },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }} onClick={(event) => event.stopPropagation()}>
        <EmtTypography variant="h4" fontWeight={800}>
          Modifier les animaux de la demande
        </EmtTypography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {selection.map((animal) => (
            <AnimalIncrement
              key={animal.slug}
              slug={animal.slug}
              count={animal.number}
              onChange={(delta) => handleCountChange(animal.slug, delta)}
            />
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: '10px' }}>
          <EmtButton fullWidth variant="outlined" size="medium" color="purple" onClick={() => setOpen(false)}>
            Annuler
          </EmtButton>
          <EmtButton
            fullWidth
            variant="contained"
            size="medium"
            color="purple"
            disabled={selectedCount === 0}
            onClick={handleSave}
          >
            Valider
          </EmtButton>
        </Box>
      </Box>
    </EmtModal>
  )
}

export default EditAnimalSelectionModal
